import { EvalModel, EvalPassFailConfig, EvalScoreConfig } from './evals-types';

interface ExemplarEvalConfig {
  reference_output?: string | null;
  s3_key?: string | null;
  s3_bucket?: string | null;
}

/** Workflow eval configs — dispatched to eval-micro-service /run-workflow-eval. */
export interface GoalAchievementEvalConfig {
  pass_threshold?: number;
  workflow_name?: string | null;
  workflow_description?: string | null;
  agent_instructions?: string | null;
}

// Three dimensions are deliberately absent, all for want of context the judge
// can't see: `groundedness` and `factual_accuracy` need source documents that
// node-level evals don't forward, and `format_compliance` scored against an
// inferred format without the upstream system prompt. Legacy configs naming
// them are dropped silently by the backend runner.
export const OUTPUT_QUALITY_DIMENSIONS = [
  'completeness',
  'conciseness',
  'coherence',
  'relevance',
] as const;

export type OutputQualityDimension = (typeof OUTPUT_QUALITY_DIMENSIONS)[number];

export interface OutputQualityEvalConfig {
  dimensions: OutputQualityDimension[];
  pass_threshold?: number;
  source_documents?: string | null;
}

export interface FactualityEvalConfig {
  pass_threshold?: number;
  source_documents?: string | null;
}

export interface ToolUseEvalConfig {
  pass_threshold?: number;
}

export const QUANTITATIVE_CHECK_DIMENSIONS = [
  'numeric_well_formedness',
  'internal_arithmetic',
  'formula_validity',
  'cross_mention_consistency',
  'range_sanity_by_label',
  'date_validity',
  'unit_consistency',
  'period_variance_arithmetic',
  'percent_breakdown_sums_to_100',
  'ratio_reconciliation',
  'cagr_consistency',
  'per_unit_arithmetic',
  'currency_code_validity',
  'inline_equation_check',
  'narrative_arithmetic',
] as const;

/** Subset of checks currently enabled in the UI. Others remain in the type
 *  union for backward-compat with saved evals but are hidden from new configs. */
// `range_sanity_by_label` is hidden — produces too many false positives
// on domain-specific values the judge can't contextualize.
export const ENABLED_QUANTITATIVE_CHECKS = [
  'numeric_well_formedness',
  // 'range_sanity_by_label',
  // Temporarily disabled
  // 'date_validity',
  'unit_consistency',
  // 'currency_code_validity',
  'narrative_arithmetic',
] as const satisfies readonly QuantitativeCheckDimension[];

export type QuantitativeCheckDimension =
  (typeof QUANTITATIVE_CHECK_DIMENSIONS)[number];

export interface QuantitativeCheckEvalConfig {
  enabled_checks: QuantitativeCheckDimension[];
  pass_threshold?: number;
}

export type EvalV2Type =
  | 'pass_fail'
  | 'score'
  | 'exemplar'
  | 'goal_achievement'
  | 'output_quality'
  | 'factuality'
  | 'tool_use'
  | 'quantitative_check';

export type EvalV2Config =
  | EvalPassFailConfig
  | EvalScoreConfig
  | ExemplarEvalConfig
  | GoalAchievementEvalConfig
  | OutputQualityEvalConfig
  | FactualityEvalConfig
  | ToolUseEvalConfig
  | QuantitativeCheckEvalConfig;

/** Set of eval types whose results are multi-dimension scored
 *  (dimension scores + overall + verdict). Distinct from criteria evals
 *  (pass_fail/score) and reference evals (exemplar). */
export const DIMENSIONAL_EVAL_TYPES: ReadonlySet<EvalV2Type> = new Set([
  'goal_achievement',
  'output_quality',
  'factuality',
  'tool_use',
  'quantitative_check',
]);

/** Backwards-compat alias. */
export const WORKFLOW_EVAL_TYPES = DIMENSIONAL_EVAL_TYPES;

/** Eval types temporarily hidden from the UI while backend work is in progress.
 *  To re-enable a type: remove it from this set, un-comment its import and
 *  TabsContent block in evaluation-config-page.tsx, and un-comment its tab
 *  trigger in the same file. */
export const HIDDEN_EVAL_TYPES = new Set<string>(['factuality', 'tool_use']);

/** Result shape returned by /v2/evaluations/{orgid}/run-dimensional-eval.
 *  `score` may be null when the dimension was not applicable to the
 *  evaluated content (e.g. a date-validity check on a doc with no
 *  dates). N/A dimensions are excluded from the overall score upstream
 *  and should be rendered with an "N/A" badge in the FE. */
export interface DimensionScore {
  score: number | null;
  explanation: string;
}

export interface DimensionalEvalResult {
  dimensions: Record<string, DimensionScore>;
  /** null when every dimension is N/A (e.g. Quantitative Check with no
   * applicable signal). Renders as an "N/A" badge on the parent card. */
  overall_score: number | null;
  overall_explanation: string;
  verdict: string;
  eval_type: string;
  metadata?: Record<string, unknown>;
  error?: string | null;
}

/** Backwards-compat alias. */
export type WorkflowEvalResult = DimensionalEvalResult;

export interface CreateEvalV2Request {
  name: string;
  description?: string;
  type: EvalV2Type;
  eval_config: EvalV2Config;
  workflow_id?: string | null;
  judge_model?: EvalModel | null;
}

export interface UpdateEvalV2Request {
  name?: string;
  description?: string;
  type?: EvalV2Type;
  eval_config?: EvalV2Config;
  workflow_id?: string | null;
  judge_model?: EvalModel | null;
}

export interface EvalV2Record {
  id: string;
  name: string;
  type: EvalV2Type;
  description?: string;
  judge_model?: EvalModel | null;
  eval_config: EvalV2Config;
  orgid: string;
  created_at: string;
  updated_at: string;
  workflow_id?: string | null;
}

export interface EvalV2UploadResponse {
  s3_key: string;
  s3_bucket: string;
  original_filename: string;
  size_bytes: number;
  content_type: string;
}

export interface EvalGuideSection {
  heading: string;
  items: string[];
}

/** Catalog metadata for an eval type. Sourced from
 *  `GET /v2/evaluations/types`; rendered under each eval type's title in the
 *  Eval Builder. `id` is a string (not narrowed to `EvalV2Type`) because the
 *  backend may return additional types (e.g. `test_vector`) that the FE does
 *  not yet render. */
export interface EvalTypeMetadata {
  id: string;
  name: string;
  description: string;
  sections: EvalGuideSection[];
  tip?: string | null;
}

// ---------------------------------------------------------------------------
// Eval results (tab / cards)
// ---------------------------------------------------------------------------

export type EvalBadgeVariant =
  | 'pass'
  | 'fail'
  | 'score-high'
  | 'score-mid'
  // Incomplete eval -- distinct from 'fail', which is a judgement.
  | 'error';

export interface EvalResultItem {
  id: string;
  title: string;
  evalTypeLabel: string;
  evalTypeId?: string;
  badgeVariant: EvalBadgeVariant;
  badgeText: string;
  detail?: string;
  /** Nested sub-dimension cards (e.g. each test inside a Reference Output eval). */
  children?: EvalResultItem[];
  /**
   * True when this result came from a parent run (rerun-from-node
   * lineage), not the run currently displayed. Surfaced in the UI as a
   * small "from previous run" badge so users see why some results
   * predate the rerun.
   */
  fromParentRun?: boolean;
}

// ---------------------------------------------------------------------------
// Eval-micro-service response shapes
// ---------------------------------------------------------------------------

export interface ReferenceOutputDimensionScore {
  score: number | null;
  explanation: string;
}

/** Response with overall_score plus arbitrary per-dimension scores. */
export interface ReferenceOutputResponse {
  overall_score: number;
  overall_explanation: string;
  error?: string | null;
  missing_elements?: string[];
  added_elements?: string[];
  [key: string]: unknown;
}

export interface PassFailResponse {
  verdict: 'PASS' | 'FAIL' | 'NA';
  explanation?: string;
}

export interface ScoreResponse {
  score: number;
  explanation: string;
}

export type EvalSingletonResponse =
  | DimensionalEvalResult
  | ReferenceOutputResponse
  | PassFailResponse
  | ScoreResponse
  | Record<string, unknown>;

// ---------------------------------------------------------------------------
// OpenSearch log shape (one document per eval node execution)
// ---------------------------------------------------------------------------

export interface SingletonResult {
  response: EvalSingletonResponse;
  source_id: string;
  id: string;
}

export interface EvalResultLog {
  eval_id: string;
  eval_name: string;
  input_results: SingletonResult[];
  error_msg?: string | null;
}

export interface EvalsResultOpensearchLog {
  id: string;
  orgid: string;
  testid: string;
  name: string;
  run_date: string;
  eval_results: EvalResultLog[];
  id_to_content: Record<string, string>;
  node_id: string;
  workflow_run_id: string;
  created_at: string;
  updated_at: string;
}
